import {
  apiErrorSchema,
  mioActionAckSchema,
  mioAgentEventSchema,
  mioAgentStateSchema,
  mioAgentTurnRequestSchema,
  mioAgentTurnResponseSchema,
  mioLearnerProfileSchema,
  mioMemorySchema,
  mioVoiceSessionSchema,
  mioVoiceCatalogSchema,
  mioMutationResultSchema,
  mioContextUpdateSchema,
  mioSpeechRequestSchema,
  mioVoiceSessionRequestSchema,
  mioVoiceAudioEventSchema,
} from '@mio/shared';

import { apiRequest } from '@/shared/api/client';
import { notifyUsageError } from '@/shared/api/usage-events';
import { env } from '@/shared/lib/env';
import { supabase } from '@/shared/lib/supabase';

import { readEventStream } from './sse.service';

import type {
  MioAgentEvent,
  MioAgentState,
  MioLearnerProfile,
  MioLiveContext,
  MioMemory,
  MioVoiceSession,
  MioVoiceAudioEvent,
} from '@mio/shared';
import type { z } from 'zod';

type TurnRequest = z.infer<typeof mioAgentTurnRequestSchema>;
export type ActionAcknowledgement = z.infer<typeof mioActionAckSchema>;

export interface MioAgentClient {
  state(signal?: AbortSignal): Promise<MioAgentState>;
  turn(request: TurnRequest, signal?: AbortSignal): Promise<{ turnId: string }>;
  events(
    threadId: string,
    after: string,
    signal: AbortSignal,
    onEvent: (event: MioAgentEvent) => void,
  ): Promise<void>;
  context(threadId: string, context: MioLiveContext): Promise<void>;
  cancel(turnId: string, playback?: { playedMs: number; generatedMs: number }): Promise<void>;
  acknowledge(actionId: string, ack: ActionAcknowledgement): Promise<void>;
  updateMemory(id: string, text: string): Promise<MioMemory>;
  deleteMemory(id: string): Promise<void>;
  resetMemory(): Promise<void>;
  updateProfile(profile: Partial<MioLearnerProfile>): Promise<MioLearnerProfile>;
  voiceSession(threadId: string): Promise<MioVoiceSession>;
  voices(): Promise<z.infer<typeof mioVoiceCatalogSchema>>;
  voiceHeartbeat(sessionId: string, context: MioLiveContext): Promise<void>;
  endVoice(sessionId: string): Promise<void>;
  newThread(): Promise<MioAgentState>;
  speech(sessionId: string, turnId: string, text: string, signal: AbortSignal): Promise<Response>;
  voiceAudio?(
    sessionId: string,
    signal: AbortSignal,
    onEvent: (event: MioVoiceAudioEvent) => void,
  ): Promise<void>;
}

const PATH = '/mio/agent';

async function authenticatedStream(path: string, signal: AbortSignal, body?: unknown) {
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new Error('Sign in again to reconnect Mio.');
  const response = await fetch(`${env.VITE_API_URL}/api/v1${PATH}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      Authorization: `Bearer ${data.session.access_token}`,
      Accept: body === undefined ? 'text/event-stream' : 'audio/pcm',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    signal,
  });
  if (!response.ok) {
    const result = apiErrorSchema.safeParse(await response.json().catch(() => null));
    notifyUsageError(result.success ? result.data.error.code : undefined);
    throw new Error(
      result.success ? result.data.error.message : 'Mio could not connect. Try again.',
    );
  }
  return response;
}

export function createMioAgentClient(): MioAgentClient {
  return {
    state: (signal) => apiRequest(`${PATH}/state`, mioAgentStateSchema, signal ? { signal } : {}),
    turn: (request, signal) =>
      apiRequest(`${PATH}/turns`, mioAgentTurnResponseSchema, {
        method: 'POST',
        body: mioAgentTurnRequestSchema.parse(request),
        ...(signal ? { signal } : {}),
      }),
    async events(threadId, after, signal, onEvent) {
      const query = new URLSearchParams({ threadId, after });
      const response = await authenticatedStream(`/events?${query}`, signal);
      if (!response.body) throw new Error('Mio returned no event stream.');
      await readEventStream(response.body, signal, (data) => {
        // An event type this bundle predates is skipped rather than ending the stream.
        const parsed = mioAgentEventSchema.safeParse(JSON.parse(data) as unknown);
        if (parsed.success) onEvent(parsed.data);
      });
    },
    async voiceAudio(sessionId, signal, onEvent) {
      const query = new URLSearchParams({ sessionId });
      const response = await authenticatedStream(`/voice/audio?${query}`, signal);
      if (!response.body) throw new Error('Mio returned no live audio stream.');
      await readEventStream(response.body, signal, (data) => {
        onEvent(mioVoiceAudioEventSchema.parse(JSON.parse(data) as unknown));
      });
    },
    async context(threadId, context) {
      await apiRequest(`${PATH}/context`, mioMutationResultSchema, {
        method: 'PUT',
        body: mioContextUpdateSchema.parse({ threadId, context }),
      });
    },
    async cancel(turnId, playback) {
      await apiRequest(`${PATH}/turns/${encodeURIComponent(turnId)}`, mioMutationResultSchema, {
        method: 'DELETE',
        ...(playback ? { body: playback } : {}),
      });
    },
    async acknowledge(actionId, ack) {
      await apiRequest(
        `${PATH}/actions/${encodeURIComponent(actionId)}/ack`,
        mioMutationResultSchema,
        {
          method: 'POST',
          body: mioActionAckSchema.parse(ack),
        },
      );
    },
    updateMemory: (id, text) =>
      apiRequest(`${PATH}/memory/${encodeURIComponent(id)}`, mioMemorySchema, {
        method: 'PATCH',
        body: { text },
      }),
    async deleteMemory(id) {
      await apiRequest(`${PATH}/memory/${encodeURIComponent(id)}`, mioMutationResultSchema, {
        method: 'DELETE',
      });
    },
    async resetMemory() {
      await apiRequest(`${PATH}/memory`, mioMutationResultSchema, { method: 'DELETE' });
    },
    updateProfile: (profile) =>
      apiRequest(`${PATH}/profile`, mioLearnerProfileSchema, { method: 'PATCH', body: profile }),
    voiceSession: (threadId) =>
      apiRequest(`${PATH}/voice/session`, mioVoiceSessionSchema, {
        method: 'POST',
        body: mioVoiceSessionRequestSchema.parse({ threadId }),
      }),
    voices: () => apiRequest(`${PATH}/voice/voices`, mioVoiceCatalogSchema),
    async voiceHeartbeat(sessionId, context) {
      const result = await apiRequest(
        `${PATH}/voice/session/${encodeURIComponent(sessionId)}`,
        mioMutationResultSchema,
        {
          method: 'PUT',
          body: { context },
        },
      );
      if (!result.ok) throw new Error('Voice session expired. Reconnect to continue.');
    },
    async endVoice(sessionId) {
      await apiRequest(
        `${PATH}/voice/session/${encodeURIComponent(sessionId)}`,
        mioMutationResultSchema,
        {
          method: 'DELETE',
        },
      );
    },
    newThread: () => apiRequest(`${PATH}/threads/new`, mioAgentStateSchema, { method: 'POST' }),
    speech: (sessionId, turnId, text, signal) =>
      authenticatedStream(
        '/voice/speech',
        signal,
        mioSpeechRequestSchema.parse({ sessionId, turnId, text }),
      ),
  };
}
